import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import TitlePages from "../../../layout/TitlePages";
import Breadcrumbs from "../../../layout/Breadcrumbs";
import Expandible from "../../../layout/HelperDataTable/Expandible";
import { useSubRowsTableConfig } from "../../../constants";
import { dataArt67 } from "../../../data/dataTransparenciaArt67";

const baseUrlPDF = "https://itetlax.org.mx/assets/pdf/transparencia/art67/";
const baseUrlExcel = "https://itetlax.org.mx/assets/excel/transparencia/art67/";

const ExcelLink = ({ baseUrl, year, urls }) => {
  return urls.map((url) => {
    const fullUrl = `${baseUrl}${year}/${url}`;
    const displayText = url.substring(0, url.lastIndexOf("."));
    return (
      <tr className="table-secondary" key={url}>
        <td>{displayText}</td>
        <td>
          <a href={fullUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faFileExcel} className="btn btn-success" />
          </a>
        </td>
      </tr>
    );
  });
};

const PdfLink = ({ baseUrl, year, urls }) => {
  return urls.map((url) => {
    const fullUrl = `${baseUrl}${year}/${url}`;
    const displayText = url.substring(0, url.lastIndexOf("."));
    return (
      <tr className="table-secondary" key={url}>
        <td>{displayText}</td>
        <td>
          <a href={fullUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faFilePdf} className="btn btn-danger" />
          </a>
        </td>
      </tr>
    );
  });
};

const Art67Table = ({ year }) => {
  const data = useMemo(() => {
    return (dataArt67[year] || []).map((item) => {
      const excels = Object.keys(item).filter((key) => key.startsWith("excel"));
      const pdfs = Object.keys(item).filter((key) => key.startsWith("pdf"));
      return { ...item, hasFiles: excels.length > 0 || pdfs.length > 0 };
    });
  }, [year]);

  useEffect(() => {
    document.title = `Artículo 67 ${year}`;
  }, [year]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "letra",
        header: "Letra",
        footer: "Letra",
        size: 50,
        enableResizing: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "descriptivo",
        header: "Descriptivo",
        footer: "Descriptivo",
        enableResizing: false,
      },
      {
        accessorKey: "cumplimiento",
        header: "Cumplimiento",
        footer: "Cumplimiento",
        enableResizing: false,
        filterFn: "equals",
        filterSelectOptions: [
          { text: "Aplica", value: "Aplica" },
          { text: "No Aplica", value: "No Aplica" },
        ],
        filterVariant: "select",
        size: 70,
      },
    ],
    []
  );

  const rootData = useMemo(() => data.filter((r) => !r.managerId), [data]);

  const renderTransparencia = ({ row }) => {
    const { managerId, letra, ...rest } = row.original;
    const excels = Object.keys(rest)
      .filter((key) => key.startsWith("excel"))
      .map((key) => rest[key]);
    const pdfs = Object.keys(rest)
      .filter((key) => key.startsWith("pdf"))
      .map((key) => rest[key]);

    if (excels.length > 0 || pdfs.length > 0) {
      return (
        <Box id="Box">
          <div className="table-responsive">
            <table className="table table-hover table-sm table-bordered table align-middle w-25">
              <thead>
                <tr>
                  <td colSpan={2}>
                    <br />
                    <strong>Fracción {letra}</strong>
                    <br />
                    <br />
                  </td>
                </tr>
              </thead>
              <tbody>
                {excels.length > 0 && (
                  <ExcelLink baseUrl={baseUrlExcel} year={year} urls={excels} />
                )}
                {pdfs.length > 0 && (
                  <PdfLink baseUrl={baseUrlPDF} year={year} urls={pdfs} />
                )}
              </tbody>
            </table>
          </div>
        </Box>
      );
    }
    return null;
  };

  const table = useSubRowsTableConfig(rootData, columns, renderTransparencia, {
    getSubRows: (row) => data.filter((r) => r.managerId === row.id),
  });

  return (
    <>
      <Breadcrumbs
        path={[
          { label: "Transparencia", url: "/Transparencia" },
          { label: "Artículo 67 Obligaciones Específicas", url: "/Articulo67" },
          { label: `Artículo 67 ${year}` },
        ]}
      />
      <TitlePages title="Transparencia" subTitle={`Artículo 67 (${year})`} />
      <Expandible />
      <MaterialReactTable table={table} />
    </>
  );
};

export default Art67Table;

Art67Table.propTypes = {
  year: PropTypes.string.isRequired,
};
